import axios from 'axios'
import { supabase } from './supabaseClient'



// PayPal configuration - now using environment variables
const PAYPAL_CLIENT_ID = process.env.VUE_APP_PAYPAL_CLIENT_ID
const PAYPAL_CLIENT_SECRET = process.env.VUE_APP_PAYPAL_CLIENT_SECRET
const PAYPAL_BASE_URL = process.env.VUE_APP_PAYPAL_BASE_URL || 'https://api.sandbox.paypal.com'

// Validate that credentials are available
if (!PAYPAL_CLIENT_ID || !PAYPAL_CLIENT_SECRET) {
  console.error('PayPal credentials not found in environment variables')
}

class PayPalService {
  constructor() {
    this.accessToken = null
    this.tokenExpiration = null
  }

  // Get PayPal access token
  async getAccessToken() {
    try {
      // Check if we have a valid token
      if (this.accessToken && this.tokenExpiration && Date.now() < this.tokenExpiration) {
        return this.accessToken
      }

      const credentials = btoa(`${PAYPAL_CLIENT_ID}:${PAYPAL_CLIENT_SECRET}`)
      
      const response = await axios.post(
        `${PAYPAL_BASE_URL}/v1/oauth2/token`,
        'grant_type=client_credentials',
        {
          headers: {
            'Authorization': `Basic ${credentials}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }
      )

      this.accessToken = response.data.access_token
      // Set expiration to 90% of actual expiration to be safe
      this.tokenExpiration = Date.now() + (response.data.expires_in * 900)
      
      return this.accessToken
    } catch (error) {
      console.error('Error getting PayPal access token:', error)
      throw new Error('Failed to authenticate with PayPal')
    }
  }

  // Create PayPal subscription plan
  async createSubscriptionPlan() {
    try {
      const accessToken = await this.getAccessToken()

      const planData = {
        product_id: await this.createProduct(),
        name: 'Smart Offer Premium - הצעה חכמה פרמיום',
        description: 'מנוי פרמיום להצעות מחיר ללא הגבלה עם תכונות מתקדמות | Premium subscription for unlimited quote generation with advanced features',
        status: 'ACTIVE',
        billing_cycles: [
          {
            frequency: {
              interval_unit: 'MONTH',
              interval_count: 1
            },
            tenure_type: 'REGULAR',
            sequence: 1,
            total_cycles: 0, // 0 means infinite
            pricing_scheme: {
              fixed_price: {
                value: '30',
                currency_code: 'ILS'
              }
            }
          }
        ],
        payment_preferences: {
          auto_bill_outstanding: true,
          setup_fee: {
            value: '0',
            currency_code: 'ILS'
          },
          setup_fee_failure_action: 'CONTINUE',
          payment_failure_threshold: 3
        }
      }

      const response = await axios.post(
        `${PAYPAL_BASE_URL}/v1/billing/plans`,
        planData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Prefer': 'return=representation'
          }
        }
      )

      return response.data
    } catch (error) {
      console.error('Error creating subscription plan:', error)
      throw new Error('Failed to create subscription plan')
    }
  }

  // Create PayPal product (required for subscription plans)
  async createProduct() {
    try {
      const accessToken = await this.getAccessToken()

      const productData = {
        name: 'Smart Offer Premium - הצעה חכמה פרמיום',
        description: 'מנוי פרמיום לכלי יצירת הצעות מחיר מתקדם | Premium subscription for advanced quote generation tool',
        type: 'SERVICE',
        category: 'SOFTWARE'
      }

      const response = await axios.post(
        `${PAYPAL_BASE_URL}/v1/catalogs/products`,
        productData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Prefer': 'return=representation'
          }
        }
      )

      return response.data.id
    } catch (error) {
      console.error('Error creating product:', error)
      throw new Error('Failed to create product')
    }
  }

  // Create subscription for user
  async createSubscription(planId, userEmail, userId) {
    try {
      const accessToken = await this.getAccessToken()

      const subscriptionData = {
        plan_id: planId,
        start_time: new Date(Date.now() + 60000).toISOString(), // Start 1 minute from now
        quantity: '1',
        shipping_amount: {
          currency_code: 'ILS',
          value: '0.00'
        },
        subscriber: {
          name: {
            given_name: userEmail.split('@')[0],
            surname: 'User'
          },
          email_address: userEmail
        },
        application_context: {
          brand_name: 'Smart Offer - הצעה חכמה',
          locale: 'he-IL',
          shipping_preference: 'NO_SHIPPING',
          user_action: 'SUBSCRIBE_NOW',
          payment_method: {
            payer_selected: 'PAYPAL',
            payee_preferred: 'IMMEDIATE_PAYMENT_REQUIRED'
          },
          return_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/subscription/cancel`,
          // Enhanced branding and user experience
          landing_page: 'BILLING',
          payment_method_preference: 'IMMEDIATE_PAYMENT_REQUIRED',
          payment_method_selected: 'PAYPAL',
          // Additional customizations for better UX
          experience_context: {
            payment_intent: 'SUBSCRIPTION',
            brand_name: 'Smart Offer - הצעה חכמה',
            locale: 'he-IL',
            shipping_preference: 'NO_SHIPPING'
          }
        },
        custom_id: userId // Store user ID for reference
      }

      const response = await axios.post(
        `${PAYPAL_BASE_URL}/v1/billing/subscriptions`,
        subscriptionData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Prefer': 'return=representation'
          }
        }
      )

      return response.data
    } catch (error) {
      console.error('Error creating subscription:', error)
      throw new Error('Failed to create subscription')
    }
  }

  // Get subscription details
  async getSubscription(subscriptionId) {
    try {
      const accessToken = await this.getAccessToken()

      const response = await axios.get(
        `${PAYPAL_BASE_URL}/v1/billing/subscriptions/${subscriptionId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      )

      return response.data
    } catch (error) {
      console.error('Error getting subscription:', error)
      throw new Error('Failed to get subscription details')
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId, reason = 'User requested cancellation') {
    try {
      const accessToken = await this.getAccessToken()

      const cancelData = {
        reason: reason
      }

      const response = await axios.post(
        `${PAYPAL_BASE_URL}/v1/billing/subscriptions/${subscriptionId}/cancel`,
        cancelData,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        }
      )

      return response.status === 204
    } catch (error) {
      console.error('Error canceling subscription:', error)
      throw new Error('Failed to cancel subscription')
    }
  }
}

// Subscription management functions for Supabase integration
export const subscriptionService = {
  // Get user's current subscription
  async getUserSubscription(userId) {
    try {
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .single()

      if (error && error.code !== 'PGRST116') { // Not found error is OK
        console.warn('Error getting user subscription, treating as free user:', error)
        return null
      }

      return data
    } catch (error) {
      console.warn('Error getting user subscription, treating as free user:', error)
      return null
    }
  },

  // Check if user can create a quote
  async canCreateQuote(userId) {
    try {
      // Get actual quote count from database
      const { data: quoteCountData, error: quoteCountError } = await supabase
        .from('quotes')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
      
      if (quoteCountError) {
        console.error('Error getting quote count:', quoteCountError)
        return false // Fail securely - don't allow creation on error
      }
      
      const actualQuoteCount = quoteCountData?.length || 0
      
      // Get subscription info
      const subscription = await this.getUserSubscription(userId)
      
      if (!subscription) {
        // No subscription record - user is on free plan with limit of 2
        return actualQuoteCount < 2
      }
      
      // Premium users have unlimited quotes
      if (subscription.subscription_type === 'premium') {
        return true
      }
      
      // Free users check against their limit
      return actualQuoteCount < subscription.quotes_limit
    } catch (error) {
      console.error('Error checking quote limit:', error)
      // Fail securely - don't allow creation if there's an error
      return false
    }
  },

  // Get subscription stats for dashboard
  async getSubscriptionStats(userId) {
    try {
      const subscription = await this.getUserSubscription(userId)
      
      // Always get actual quote count from database
      const { data: quoteCountData, error: quoteCountError } = await supabase
        .from('quotes')
        .select('id', { count: 'exact' })
        .eq('user_id', userId)
      
      if (quoteCountError) {
        console.error('Error getting quote count for stats:', quoteCountError)
        // Return conservative stats on error
        return {
          subscriptionType: 'free',
          quotesUsed: 0,
          quotesLimit: 2,
          quotesRemaining: 2,
          canCreateQuote: false, // Conservative approach
          status: 'active'
        }
      }
      
      const actualQuoteCount = quoteCountData?.length || 0
      
      if (!subscription) {
        // No subscription record - user is on free plan
        const freeLimit = 2
        return {
          subscriptionType: 'free',
          quotesUsed: actualQuoteCount,
          quotesLimit: freeLimit,
          quotesRemaining: Math.max(0, freeLimit - actualQuoteCount),
          canCreateQuote: actualQuoteCount < freeLimit,
          status: 'active'
        }
      }

      const quotesRemaining = subscription.subscription_type === 'premium' 
        ? -1 // Unlimited
        : Math.max(0, subscription.quotes_limit - actualQuoteCount)

      
      return {
        subscriptionType: subscription.subscription_type,
        planType: subscription.plan_type || 'monthly', // Include plan type
        quotesUsed: actualQuoteCount,
        quotesLimit: subscription.quotes_limit,
        quotesRemaining,
        canCreateQuote: subscription.subscription_type === 'premium' || quotesRemaining > 0,
        status: subscription.status,
        paypalSubscriptionId: subscription.paypal_subscription_id,
        currentPeriodStart: subscription.current_period_start,
        currentPeriodEnd: subscription.current_period_end,
        canceledAt: subscription.canceled_at
      }
    } catch (error) {
      console.error('Error getting subscription stats:', error)
      // Return conservative free plan stats as fallback
      return {
        subscriptionType: 'free',
        quotesUsed: 0,
        quotesLimit: 2,
        quotesRemaining: 0, // Conservative - don't allow creation
        canCreateQuote: false, // Conservative approach
        status: 'active'
      }
    }
  },

  // Update subscription after PayPal success
  async updateSubscriptionAfterPayment(userId, paypalSubscriptionId, paypalPayerId, planType = 'monthly') {
    try {
      // Use the secure RPC function - paypal_payer_id is optional now
      const payload = {
        paypal_subscription_id: paypalSubscriptionId,
        plan_type: planType
      }
      
      // Only add paypal_payer_id if it exists
      if (paypalPayerId) {
        payload.paypal_payer_id = paypalPayerId
      }
      
      const { data, error } = await supabase.rpc('rpc_upgrade_user_subscription', payload)

      if (error) {
        console.error('Error upgrading subscription:', error)
        throw error
      }

      console.log('Subscription upgraded successfully:', data)
      return data
    } catch (error) {
      console.error('Error updating subscription:', error)
      throw error
    }
  },

  // Cancel user subscription
  async cancelUserSubscription(userId, reason = 'User requested cancellation') {
    try {
      const subscription = await this.getUserSubscription(userId)
      
      if (!subscription || !subscription.paypal_subscription_id) {
        throw new Error('No active PayPal subscription found')
      }

      // Cancel with PayPal first
      const paypalService = new PayPalService()
      await paypalService.cancelSubscription(subscription.paypal_subscription_id, reason)

      // Update in database using secure RPC function
      const { data, error } = await supabase.rpc('rpc_cancel_user_subscription', {
        reason: reason
      })

      if (error) {
        console.error('Error canceling subscription in database:', error)
        throw error
      }

      console.log('Subscription canceled successfully:', data)
      return data
    } catch (error) {
      console.error('Error canceling subscription:', error)
      throw error
    }
  }
}

export default PayPalService